import { useState, useMemo, useEffect } from 'react'
import {
  Container,
  Title,
  Stack,
  Text,
  Group,
  Button,
  Loader,
  Center,
  Badge,
  Table,
  Paper,
  TextInput,
  MultiSelect,
  ScrollArea,
  Pagination,
  Tooltip,
  RingProgress,
  SimpleGrid,
  SegmentedControl,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  IconCalendar,
  IconRefresh,
  IconSearch,
  IconDownload,
  IconLoader,
} from '@tabler/icons-react'
import { useSearchDashboard } from '@/hooks/useAnalytics'
import { exportSearchDashboard } from '@/services/analytics.api'
import { useVersionContext } from '@/context/VersionContext'
import type {
  SearchDashboardData,
  SearchDashboardMetrics,
  SearchQueueBreakdownItem,
  SearchQueueByTypeRow,
} from '@/types'
import { SearchDetailDialog } from '@/components/SearchDetailDialog'

const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString()
const pct = (part: number, total: number) => total > 0 ? ((part / total) * 100).toFixed(1) : '0.0'
const SEARCH_QUEUE_COLORS: Record<string, string> = {
  'Manual Review': 'orange',
  'Insufficient Data': 'orange',
  Auditor: 'blue',
  Exclusion: 'gray',
  Automation: 'teal',
  'Other / Unknown': 'yellow',
  'Ready for QA': 'grape',
}

function MultiValueCell({ value, color = 'gray' }: { value?: string | null; color?: string }) {
  if (!value) return <Text size="sm" c="dimmed">-</Text>
  const items = value.split(',').map(s => s.trim()).filter(Boolean)
  if (items.length === 0) return <Text size="sm" c="dimmed">-</Text>
  if (items.length === 1) return <Badge variant="light" color={color} size="xs">{items[0]}</Badge>
  return (
    <Tooltip label={items.join(' · ')} multiline w={220}>
      <Group gap={4} wrap="nowrap">
        <Badge variant="light" color={color} size="xs">{items[0]}</Badge>
        <Badge variant="outline" color="gray" size="xs">+{items.length - 1}</Badge>
      </Group>
    </Tooltip>
  )
}

function RoutingBreakdown({ search }: { search: SearchDashboardData }) {
  const auto = search.queue_auto_count || 0
  const qa = search.queue_qa_count || 0
  const research = search.queue_research_count || 0
  const total = auto + qa + research
  if (total === 0) return <Text size="sm" c="dimmed">-</Text>
  const parts: { label: string; count: number; color: string; short: string }[] = []
  if (auto > 0) parts.push({ label: 'Automated', count: auto, color: 'teal', short: 'A' })
  if (qa > 0) parts.push({ label: 'QA Review', count: qa, color: 'blue', short: 'Q' })
  if (research > 0) parts.push({ label: 'Research', count: research, color: 'orange', short: 'R' })
  const tooltip = parts.map(p => `${p.label}: ${p.count}`).join(' · ')
  return (
    <Tooltip label={tooltip}>
      <Group gap={4} wrap="nowrap">
        {parts.map(p => (
          <Badge key={p.short} variant="light" color={p.color} size="xs">
            {p.short}:{p.count}
          </Badge>
        ))}
      </Group>
    </Tooltip>
  )
}

function DonutChart({ metrics }: { metrics: SearchDashboardMetrics }) {
  const total = metrics.totalOffenses
  const reportable = metrics.reportableOffenses
  const notReportable = metrics.notReportableOffenses
  const other = Math.max(0, total - reportable - notReportable)

  const slices = [
    { label: 'Reportable',       value: reportable,    color: '#fa5252' },
    { label: 'Not Reportable',   value: notReportable, color: '#40c057' },
    { label: 'Pending / Other',  value: other,         color: '#fab005' },
  ]

  const ringData = slices
    .filter(s => s.value > 0)
    .map(s => ({
      value: total > 0 ? (s.value / total) * 100 : 0,
      color: s.color,
      tooltip: `${s.label}: ${fmt(s.value)}`,
    }))

  return (
    <Paper p="lg" withBorder>
      <Text fw={600} size="sm" mb="md">Offense Breakdown</Text>
      <Group justify="center" gap={48} wrap="wrap">
        <RingProgress
          size={200}
          thickness={24}
          roundCaps
          sections={ringData}
          label={
            <Stack align="center" gap={0}>
              <Text size="xl" fw={800} ta="center">{fmt(total)}</Text>
              <Text size="xs" c="dimmed" ta="center">Total Offenses</Text>
            </Stack>
          }
        />
        <Stack gap="sm">
          {slices.map(s => (
            <Group key={s.label} gap="sm">
              <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: s.color, flexShrink: 0 }} />
              <Text size="sm" fw={500} style={{ minWidth: 140 }}>{s.label}</Text>
              <Text size="sm" fw={700}>{fmt(s.value)}</Text>
              <Text size="xs" c="dimmed">({pct(s.value, total)}%)</Text>
            </Group>
          ))}
        </Stack>
      </Group>
    </Paper>
  )
}

function KeyMetrics({ metrics }: { metrics: SearchDashboardMetrics }) {
  const cards = [
    { label: 'Total Searches',  value: fmt(metrics.totalSearches),           color: '#1E3A5F', subtitle: '' },
    { label: 'Total Offenses',  value: fmt(metrics.totalOffenses),           color: '#228be6', subtitle: '' },
    { label: 'Reportable Rate', value: `${metrics.reportableRate}%`,          color: '#2ECC71', subtitle: '' },
    { label: 'Avg Turnaround',  value: `${metrics.avgTurnaroundDays} days`,  color: '#9b59b6', subtitle: '' },
  ]

  return (
    <Group grow gap="md" wrap="wrap">
      {cards.map(c => (
        <Paper key={c.label} p="md" withBorder radius="md" shadow="xs" miw={140}>
          <Stack align="center" gap={4}>
            <Text size="1.5rem" fw={700} style={{ color: c.color }}>{c.value}</Text>
            <Text size="xs" c="dimmed">{c.label}</Text>
            {c.subtitle && <Text size="xs" c="dimmed" ta="center">{c.subtitle}</Text>}
          </Stack>
        </Paper>
      ))}
    </Group>
  )
}

const QUEUE_DISPLAY_LABEL: Record<string, string> = {
  'Insufficient Data': 'Manual Review',
}

function SearchQueueBreakdownSection({
  title,
  subtitle,
  items,
}: {
  title: string
  subtitle: string
  items: SearchQueueBreakdownItem[]
}) {
  const visibleItems = items.filter(item => item.searchQueue !== 'Other / Unknown' && item.searchCount > 0)
  if (visibleItems.length === 0) return null

  return (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        <div>
          <Text fw={600} size="sm">{title}</Text>
          <Text size="xs" c="dimmed">{subtitle}</Text>
        </div>
        <SimpleGrid cols={{ base: 1, sm: 2, xl: Math.min(items.length, 4) }}>
          {visibleItems.map(item => {
            const displayLabel = QUEUE_DISPLAY_LABEL[item.searchQueue] || item.searchQueue
            return (
              <Paper key={item.searchQueue} p="md" withBorder radius="md">
                <Stack gap={6}>
                  <Group justify="space-between" align="flex-start">
                    <Badge
                      variant="light"
                      color={SEARCH_QUEUE_COLORS[displayLabel] || SEARCH_QUEUE_COLORS[item.searchQueue] || 'gray'}
                      size="sm"
                    >
                      {displayLabel}
                    </Badge>
                    <Text size="xl" fw={700}>
                      {fmt(item.searchCount)}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {item.pct.toFixed(1)}% of searches
                  </Text>
                  <Text size="xs" c="dimmed">
                    Avg offenses/search: {item.avgOffensesPerSearch.toFixed(1)}
                  </Text>
                </Stack>
              </Paper>
            )
          })}
        </SimpleGrid>
      </Stack>
    </Paper>
  )
}

function SearchQueueByTypeTable({ rows }: { rows: SearchQueueByTypeRow[] }) {
  if (rows.length === 0) return null

  return (
    <ScrollArea>
      <Table withTableBorder withColumnBorders striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Search Type</Table.Th>
            <Table.Th>Manual Review</Table.Th>
            <Table.Th>Auditor</Table.Th>
            <Table.Th>Exclusion</Table.Th>
            <Table.Th>Automation</Table.Th>
            <Table.Th>Total</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(row => (
            <Table.Tr key={row.searchType}>
              <Table.Td>
                <Text size="sm" fw={500}>{row.searchType}</Text>
              </Table.Td>
              <Table.Td>{fmt(row.insufficientData)}</Table.Td>
              <Table.Td>{fmt(row.auditor)}</Table.Td>
              <Table.Td>{fmt(row.exclusion)}</Table.Td>
              <Table.Td>{fmt(row.automation)}</Table.Td>
              <Table.Td>
                <Text fw={600}>{fmt(row.total)}</Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  )
}

function SearchQueueByTypeChart({ rows }: { rows: SearchQueueByTypeRow[] }) {
  if (rows.length === 0) return null

  const chartData = rows.map(row => {
    const total = row.total || 1
    return {
      searchType: row.searchType,
      total: row.total,
      insufficientPct: Number(((row.insufficientData / total) * 100).toFixed(1)),
      auditorPct: Number(((row.auditor / total) * 100).toFixed(1)),
      exclusionPct: Number(((row.exclusion / total) * 100).toFixed(1)),
      automationPct: Number(((row.automation / total) * 100).toFixed(1)),
      otherPct: Number(((row.other / total) * 100).toFixed(1)),
      insufficient: row.insufficientData,
      auditor: row.auditor,
      exclusion: row.exclusion,
      automation: row.automation,
    }
  })

  const chartHeight = Math.max(360, chartData.length * 42)

  return (
    <Stack gap="md">
      <div style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 8, right: 24, left: 24, bottom: 8 }}
            barCategoryGap={8}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis
              type="category"
              dataKey="searchType"
              width={180}
              tick={{ fontSize: 12 }}
            />
            <RechartsTooltip
              formatter={(value: number, name: string, payload: any) => {
                const countKey = name.replace('Pct', '')
                const count = payload?.payload?.[countKey] ?? 0
                return [`${value}% (${fmt(count)})`, name.replace('Pct', '')]
              }}
              labelFormatter={(_, payload) => {
                const row = payload?.[0]?.payload
                return row ? `${row.searchType} · ${fmt(row.total)} searches` : ''
              }}
            />
            <Legend
              formatter={(value) => value.replace('Pct', '')}
            />
            <Bar dataKey="insufficientPct" name="Manual Review" stackId="queue" fill="#f59e0b" />
            <Bar dataKey="auditorPct" name="Auditor" stackId="queue" fill="#3b82f6" />
            <Bar dataKey="exclusionPct" name="Exclusion" stackId="queue" fill="#6b7280" />
            <Bar dataKey="automationPct" name="Automation" stackId="queue" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Stack>
  )
}

function OffenseSplitBar({ search }: { search: SearchDashboardData }) {
  const total = search.total_offenses
  if (total === 0) return <Text size="sm" c="dimmed">-</Text>

  const segments = [
    { count: search.reportable_count,         color: '#fa5252', label: 'Reportable' },
    { count: search.not_reportable_count,     color: '#40c057', label: 'Non-reportable' },
    { count: search.requires_research_count,  color: '#fab005', label: 'Researcher' },
  ]

  return (
    <Tooltip label={segments.map(s => `${s.label}: ${s.count}`).join(' | ')}>
      <div style={{ display: 'flex', width: 80, height: 14, borderRadius: 4, overflow: 'hidden', border: '1px solid #dee2e6' }}>
        {segments.map((s, i) => {
          const w = (s.count / total) * 100
          if (w === 0) return null
          return <div key={i} style={{ width: `${w}%`, backgroundColor: s.color, minWidth: s.count > 0 ? 2 : 0 }} />
        })}
      </div>
    </Tooltip>
  )
}

export function SearchDashboard() {
  const [selectedSearch, setSelectedSearch] = useState<SearchDashboardData | null>(null)
  const [dialogOpened, setDialogOpened] = useState(false)
  const [queueByTypeView, setQueueByTypeView] = useState<'chart' | 'table' | 'both'>('both')

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(2026, 5, 1),
    new Date(2026, 5, 30),
  ])
  const [searchId, setSearchId] = useState('')
  const [searchTypeFilter, setSearchTypeFilter] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const pageSize = 50
  const [isExporting, setIsExporting] = useState(false)

  const { selectedVersion } = useVersionContext()

  const { data: dashboard, isLoading, refetch, isFetching } = useSearchDashboard({
    startDate: dateRange[0]?.toISOString().split('T')[0] || '',
    endDate: dateRange[1]?.toISOString().split('T')[0] || '',
    page,
    pageSize,
    searchId: searchId || undefined,
    searchType: searchTypeFilter.length > 0 ? searchTypeFilter : undefined,
    includeMetrics: page === 1,
    modelVersion: selectedVersion || undefined,
  })

  useEffect(() => {
    setPage(1)
  }, [dateRange, searchId, searchTypeFilter, selectedVersion])

  const metrics: SearchDashboardMetrics | null = dashboard?.metrics || null
  const rows: SearchDashboardData[] = dashboard?.rows || []
  const totalCount = dashboard?.totalCount || 0
  const totalPages = Math.ceil(totalCount / pageSize)

  const searchTypeOptions = useMemo(() => {
    if (!metrics?.searchTypeDistribution) return []
    return metrics.searchTypeDistribution.map(st => ({
      value: st.searchType,
      label: `${st.searchType} (${st.searchCount})`,
    }))
  }, [metrics])

  const handleRowClick = (search: SearchDashboardData) => {
    setSelectedSearch(search)
    setDialogOpened(true)
  }

  const downloadCSV = async () => {
    if (totalCount === 0) return
    setIsExporting(true)
    try {
      const exportResponse = await exportSearchDashboard({
        startDate: dateRange[0]?.toISOString().split('T')[0] || '',
        endDate: dateRange[1]?.toISOString().split('T')[0] || '',
        searchId: searchId || undefined,
        searchType: searchTypeFilter.length > 0 ? searchTypeFilter : undefined,
      })

      const allRows = exportResponse.rows || []
      if (allRows.length === 0) {
        setIsExporting(false)
        return
      }

      const columns = [
        'search_id', 'search_type', 'total_offenses',
        'reportable_count', 'not_reportable_count', 'identity_level',
        'queue_auto_count', 'queue_qa_count', 'queue_research_count',
      ]
      const header = columns.join(',')
      const csvRows = allRows.map((row: any) =>
        columns.map(col => {
          const val = row[col]
          if (val === null || val === undefined) return ''
          const s = String(val)
          return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s
        }).join(',')
      )

      const csv = [header, ...csvRows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `search_dashboard_${dateRange[0]?.toISOString().split('T')[0]}_${dateRange[1]?.toISOString().split('T')[0]}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Title order={2}>Search Analytics</Title>

        {/* Filters */}
        <Paper p="md" withBorder>
          <Group gap="md" align="flex-end" wrap="wrap">
            <DatePickerInput
              type="range"
              label="Search Date Range"
              placeholder="Pick date range"
              value={dateRange}
              onChange={setDateRange}
              leftSection={<IconCalendar size={16} />}
              style={{ minWidth: 260 }}
            />
            <TextInput
              label="Search ID"
              placeholder="Filter by search ID..."
              value={searchId}
              onChange={(e) => setSearchId(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              style={{ minWidth: 200 }}
            />
            <MultiSelect
              label="Search Type"
              placeholder="All types"
              data={searchTypeOptions}
              value={searchTypeFilter}
              onChange={setSearchTypeFilter}
              clearable
              searchable
              style={{ minWidth: 220 }}
            />
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
              loading={isFetching}
            >
              Refresh
            </Button>
            <Tooltip label={`Export all ${fmt(totalCount)} searches as CSV`}>
              <Button
                variant="outline"
                leftSection={isExporting ? <IconLoader size={16} /> : <IconDownload size={16} />}
                onClick={downloadCSV}
                disabled={totalCount === 0 || isExporting}
                loading={isExporting}
              >
                {isExporting ? 'Exporting...' : `Export (${fmt(totalCount)})`}
              </Button>
            </Tooltip>
          </Group>
        </Paper>

        {isLoading && !dashboard ? (
          <Center py="xl"><Loader size="lg" /></Center>
        ) : (
          <>
            {isFetching && (
              <Paper p="xs" withBorder radius="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                <Group gap="xs" justify="center">
                  <Loader size="xs" />
                  <Text size="sm" c="blue">Loading results...</Text>
                </Group>
              </Paper>
            )}

            {/* Key Metrics */}
            {metrics && <KeyMetrics metrics={metrics} />}

            {metrics && metrics.totalSearches > 0 && (
              <SearchQueueBreakdownSection
                title="Search Queue Breakdown"
                subtitle="How searches roll up after offense-level routing is aggregated to a single search outcome."
                items={metrics.searchQueueBreakdown}
              />
            )}

            {metrics && metrics.totalSearches > 0 && (
              <SearchQueueBreakdownSection
                title="Ready for QA Summary"
                subtitle="Simplified view of searches that still need manual review versus searches ready for downstream review."
                items={metrics.searchQueueReadyBreakdown}
              />
            )}

            {metrics && metrics.totalSearches > 0 && metrics.searchQueueByType.length > 0 && (
              <Paper p="lg" withBorder>
                <Stack gap="md">
                  <Group justify="space-between" align="flex-end">
                    <div>
                      <Text fw={600} size="sm">Queue Breakdown by Search Type</Text>
                      <Text size="xs" c="dimmed">
                        Search-level queue outcomes by search type, ordered by total search volume.
                      </Text>
                    </div>
                    <SegmentedControl
                      size="xs"
                      value={queueByTypeView}
                      onChange={(value) => setQueueByTypeView(value as 'chart' | 'table' | 'both')}
                      data={[
                        { label: 'Chart', value: 'chart' },
                        { label: 'Table', value: 'table' },
                        { label: 'Both', value: 'both' },
                      ]}
                    />
                  </Group>

                  {(queueByTypeView === 'chart' || queueByTypeView === 'both') && (
                    <SearchQueueByTypeChart rows={metrics.searchQueueByType} />
                  )}

                  {(queueByTypeView === 'table' || queueByTypeView === 'both') && (
                    <SearchQueueByTypeTable rows={metrics.searchQueueByType} />
                  )}
                </Stack>
              </Paper>
            )}

            {/* Donut Chart */}
            {metrics && <DonutChart metrics={metrics} />}

            {/* Results info */}
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Showing {rows.length} of {fmt(totalCount)} searches
                {isFetching && <Loader size="xs" ml="xs" />}
              </Text>
              {totalPages > 1 && (
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  size="sm"
                />
              )}
            </Group>

            {/* Search Table */}
            <Paper withBorder>
              <ScrollArea>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Search ID</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Offenses</Table.Th>
                      <Table.Th>Offense Breakdown</Table.Th>
                      <Table.Th>Reportable</Table.Th>
                      <Table.Th>Not Reportable</Table.Th>
                      <Table.Th>Identity</Table.Th>
                      <Table.Th>Routing</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={8}>
                          <Text ta="center" c="dimmed" py="xl">No searches found for the selected filters.</Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      rows.map((search, i) => (
                        <Table.Tr
                          key={search.search_id || i}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(search)}
                        >
                          <Table.Td>
                            <Text size="sm" ff="monospace" c="blue" fw={500}>
                              {search.search_id}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {search.search_type ? (
                              <Badge variant="light" size="xs">{search.search_type}</Badge>
                            ) : '-'}
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="filled" size="xs" color="blue">{search.total_offenses}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <OffenseSplitBar search={search} />
                          </Table.Td>
                          <Table.Td>
                            {search.reportable_count > 0 ? (
                              <Badge variant="light" color="red" size="xs">{search.reportable_count}</Badge>
                            ) : <Text size="sm" c="dimmed">0</Text>}
                          </Table.Td>
                          <Table.Td>
                            {search.not_reportable_count > 0 ? (
                              <Badge variant="light" color="green" size="xs">{search.not_reportable_count}</Badge>
                            ) : <Text size="sm" c="dimmed">0</Text>}
                          </Table.Td>
                          <Table.Td>
                            <MultiValueCell value={search.identity_level} color="cyan" />
                          </Table.Td>
                          <Table.Td>
                            <RoutingBreakdown search={search} />
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>

            {/* Bottom pagination */}
            {totalPages > 1 && (
              <Center>
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  size="sm"
                />
              </Center>
            )}
          </>
        )}
      </Stack>

      <SearchDetailDialog
        opened={dialogOpened}
        onClose={() => setDialogOpened(false)}
        search={selectedSearch}
      />
    </Container>
  )
}
